/**
 * SyncTab - External service connections
 *
 * Contains:
 * - Intervals.icu OAuth connection
 * - Sync to calendar toggle
 * - Connection status
 */

import {
  Check as CheckIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import type { UserProfile } from '@shared/types';
import React, { useEffect, useState } from 'react';
import { useOAuthPopup } from '../../../../hooks/useOAuthPopup';
import apiClient from '../../../../services/api';
import { logger } from '../../../../utils/logger';

interface SyncTabProps {
  profile: UserProfile;
  onUpdate: (updates: Partial<UserProfile>, requiresConfirmation?: boolean) => void;
  isConnected: boolean;
  onRefreshAuth: () => Promise<void>;
}

const SyncTab: React.FC<SyncTabProps> = ({ profile, onUpdate, isConnected, onRefreshAuth }) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { initiateOAuth } = useOAuthPopup();

  // Listen for OAuth completion
  useEffect(() => {
    const handleOAuthComplete = async () => {
      logger.info('intervals-connected event received in SyncTab');
      await new Promise(resolve => setTimeout(resolve, 500));

      try {
        const profileResponse = await apiClient.getUserProfile();
        if (profileResponse.success && profileResponse.data) {
          onUpdate(profileResponse.data, false);
          await onRefreshAuth();
        }
      } catch (err) {
        logger.error('Error refreshing after OAuth', err as Error);
      }
      setIsConnecting(false);
    };

    window.addEventListener('intervals-connected', handleOAuthComplete);
    return () => window.removeEventListener('intervals-connected', handleOAuthComplete);
  }, [onUpdate, onRefreshAuth]);

  const handleConnect = async () => {
    setIsConnecting(true);
    setError(null);

    try {
      await initiateOAuth();

      // Check if already connected
      setTimeout(async () => {
        try {
          const profileResponse = await apiClient.getUserProfile();
          if (profileResponse.success && profileResponse.data?.intervalsConnected) {
            onUpdate(profileResponse.data, false);
            await onRefreshAuth();
          }
        } catch (err) {
          logger.error('Error checking connection status', err as Error);
        }
        setIsConnecting(false);
      }, 500);
    } catch (err) {
      logger.error('Failed to connect to Intervals.icu', err as Error);
      setError(err instanceof Error ? err.message : 'Failed to connect');
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setIsConnecting(true);
    setError(null);

    try {
      const response = await apiClient.unlinkIntervalsAccount();
      if (response.success) {
        onUpdate(
          {
            intervalsConnected: false,
            intervalsAthleteId: undefined,
          },
          false
        );
        await onRefreshAuth();
      } else {
        throw new Error(response.error || 'Failed to disconnect');
      }
    } catch (err) {
      logger.error('Failed to disconnect', err as Error);
      setError(err instanceof Error ? err.message : 'Failed to disconnect');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleSyncToggle = (enabled: boolean) => {
    onUpdate({ syncToIntervals: enabled }, false);
  };

  const handleManualSync = async () => {
    setIsSyncing(true);
    setError(null);
    setSyncSuccess(null);

    try {
      // Fetch current training plan
      const planResponse = await apiClient.getStoredTrainingPlan();
      if (!planResponse.success || !planResponse.data) {
        throw new Error('No training plan found. Generate a plan first.');
      }

      // Sync to Intervals.icu
      const syncResponse = await apiClient.syncTrainingPlanToIntervals(planResponse.data);
      if (syncResponse.success && syncResponse.data) {
        const { successfulSyncs, failedSyncs } = syncResponse.data;
        setSyncSuccess(
          `Successfully synced ${successfulSyncs} workout${successfulSyncs !== 1 ? 's' : ''} to Intervals.icu${failedSyncs > 0 ? ` (${failedSyncs} failed)` : ''}`
        );

        // Clear success message after 5 seconds
        setTimeout(() => setSyncSuccess(null), 5000);
      } else {
        throw new Error(syncResponse.error || 'Failed to sync training plan');
      }
    } catch (err) {
      logger.error('Failed to sync training plan', err as Error);
      setError(err instanceof Error ? err.message : 'Failed to sync training plan');
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <Box>
      {/* Section Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          🔗 Sync & Connections
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Connect external services to sync your training data.
        </Typography>
      </Box>

      {/* Intervals.icu Connection Card */}
      <Card
        variant="outlined"
        sx={{
          borderColor: isConnected ? 'success.main' : 'divider',
          borderWidth: isConnected ? 2 : 1,
        }}
      >
        <CardContent>
          {/* Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <img
                src="/icons/intervals.png"
                alt="Intervals.icu"
                style={{
                  width: 32,
                  height: 32,
                  opacity: isConnected ? 1 : 0.5,
                }}
              />
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Intervals.icu
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Training analytics platform
                </Typography>
              </Box>
            </Box>
            {isConnected && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                <Typography variant="caption" color="success.main" fontWeight={600}>
                  Connected
                </Typography>
              </Box>
            )}
          </Box>

          {/* Features */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              When connected, PacePilot will:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              <Typography component="li" variant="body2" color="text.secondary">
                Sync ATL, CTL, HRV metrics automatically
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Push planned workouts to your calendar
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Import your training history
              </Typography>
            </Box>
          </Box>

          {/* Sync Toggle (only when connected) */}
          {isConnected && (
            <>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  mb: 2,
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={profile.syncToIntervals !== false}
                      onChange={e => handleSyncToggle(e.target.checked)}
                      aria-label="Sync workouts to Intervals.icu calendar"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Sync Workouts to Calendar
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Automatically push planned workouts to Intervals.icu
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, width: '100%' }}
                />
              </Box>

              {/* Manual Sync Button */}
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleManualSync}
                  disabled={isSyncing}
                  startIcon={isSyncing ? <CircularProgress size={16} /> : <SyncIcon />}
                  aria-label="Manually sync current training plan to Intervals.icu"
                >
                  {isSyncing ? 'Syncing...' : 'Sync Current Plan to Intervals.icu'}
                </Button>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5, px: 1 }}
                >
                  Manually sync your current training plan (next 10 days) to your calendar
                </Typography>
              </Box>
            </>
          )}

          {/* Athlete ID (when connected) */}
          {isConnected && profile.intervalsAthleteId && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Athlete ID: {profile.intervalsAthleteId}
              </Typography>
            </Box>
          )}

          {/* Error message */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Success message */}
          {syncSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {syncSuccess}
            </Alert>
          )}

          {/* Connect/Disconnect Button */}
          {isConnected ? (
            <Button
              variant="outlined"
              color="error"
              fullWidth
              onClick={handleDisconnect}
              disabled={isConnecting}
              startIcon={isConnecting ? <CircularProgress size={16} /> : <LinkOffIcon />}
              aria-label="Disconnect from Intervals.icu"
            >
              {isConnecting ? 'Disconnecting...' : 'Disconnect'}
            </Button>
          ) : (
            <Button
              variant="contained"
              fullWidth
              onClick={handleConnect}
              disabled={isConnecting}
              startIcon={isConnecting ? <CircularProgress size={16} /> : <LinkIcon />}
              aria-label="Connect to Intervals.icu"
            >
              {isConnecting ? 'Connecting...' : 'Connect to Intervals.icu'}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Future Integrations Placeholder */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Coming Soon
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1}>
          {['Strava', 'Garmin Connect', 'TrainingPeaks', 'Wahoo'].map(service => (
            <Box
              key={service}
              sx={{
                px: 2,
                py: 1,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                borderStyle: 'dashed',
                opacity: 0.5,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {service}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Help text */}
      <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">
          💡 Tip: Connecting to Intervals.icu allows for more accurate training load calculations
          based on your actual workout data.
        </Typography>
      </Box>
    </Box>
  );
};

export default SyncTab;
